import type {
  Course,
  BudgetResult,
  GradeImpact,
  RecoveryPath,
  RiskLevel,
  CalendarDay,
  PenaltyTier,
  AbsenceCounts,
  SessionType as SessionTypeT,
} from '@abs/shared-types';
import { AbsenceType, SessionOverrideType, SessionType } from '@abs/shared-types';
import { computeTotalSessions, getRemainingSessionCount } from '@abs/shared-logic';
import { computeFullBudget, computeEffectiveAbsences, computeMaxAbsences } from '@abs/shared-logic';
import { resolveActiveTier } from '@abs/shared-logic';
import { computeGradeImpact } from '@abs/shared-logic';
import { computeRecoveryPath, deriveWarningThreshold } from '@abs/shared-logic';
import { simulateFutureAbsences, backCalculateMaxAbsences } from '@abs/shared-logic';
import { projectPace, projectAutoFailDate, projectFinalGradeAtPace } from '@abs/shared-logic';
import type { PaceProjection } from '@abs/shared-logic';
import { rankRemainingSessions } from '@abs/shared-logic';
import type { RankedSession } from '@abs/shared-logic';
import { letterGradeToGPA } from '@abs/shared-logic';
import { getMonthCalendar, todayISO, getDayOfWeek, parseISODate } from '@abs/shared-logic';
import { RiskLevel as RiskLevelEnum, AbsenceReason } from '@abs/shared-types';
import type { AbsenceEntry } from '@abs/shared-types';
import type { AppStore } from './index';

type State = AppStore;

// ── Helper: Get course, policy, and entries ──────────────────────────────

function getCourseData(state: State, courseId: string) {
  const course = state.courses.find((c) => c.id === courseId);
  if (!course) return null;

  const policy = state.policies.find((p) => p.id === course.policyId);
  if (!policy) return null;

  const entries = state.entries.filter((e) => e.courseId === courseId);
  const { total, sessions } = computeTotalSessions(
    course.meetingPatterns,
    course.sessionOverrides,
    course.startDate,
    course.endDate,
  );

  return { course, policy, entries, totalSessions: total, sessions };
}

// ── Selectors ────────────────────────────────────────────────────────────

/** Select onboarding completed status */
export function selectOnboardingCompleted(state: State): boolean {
  return state.settings.onboardingCompleted;
}

/** Select absence counts by type for a course */
export function selectAbsenceCounts(state: State, courseId: string): AbsenceCounts {
  const entries = state.entries.filter((e) => e.courseId === courseId);

  return {
    unexcused: entries.filter((e) => e.type === AbsenceType.Unexcused).length,
    excused: entries.filter((e) => e.type === AbsenceType.Excused).length,
    late: entries.filter((e) => e.type === AbsenceType.Late).length,
    earlyDepartures: entries.filter((e) => e.type === AbsenceType.EarlyDeparture).length,
    present: entries.filter((e) => e.type === AbsenceType.Present).length,
    total: entries.length,
  };
}

/** Select full budget for a course */
export function selectCourseBudget(state: State, courseId: string): BudgetResult | null {
  const data = getCourseData(state, courseId);
  if (!data) return null;

  return computeFullBudget(data.course, data.policy, data.entries, data.totalSessions);
}

/** Select grade impact for a course */
export function selectGradeImpact(state: State, courseId: string): GradeImpact | null {
  const data = getCourseData(state, courseId);
  if (!data) return null;

  const effectiveAbsences = computeEffectiveAbsences(data.entries, data.policy);

  return computeGradeImpact(
    data.course.baseGrade,
    data.course.gradingScale,
    data.policy,
    effectiveAbsences,
    data.entries,
    data.course.creditHours,
  );
}

/** Select recovery path for a course */
export function selectRecoveryPath(state: State, courseId: string): RecoveryPath | null {
  const data = getCourseData(state, courseId);
  if (!data) return null;

  const effectiveAbsences = computeEffectiveAbsences(data.entries, data.policy);
  const attendedSessions = data.totalSessions - effectiveAbsences.totalPenaltyAbsences;
  const remainingSessions = getRemainingSessionCount(data.sessions);
  const warningThreshold = deriveWarningThreshold(data.totalSessions, data.policy.tiers);

  return computeRecoveryPath(
    data.totalSessions,
    attendedSessions,
    remainingSessions,
    warningThreshold,
  );
}

/** Select risk level for a course */
export function selectRiskLevel(state: State, courseId: string): RiskLevel | null {
  const budget = selectCourseBudget(state, courseId);
  return budget?.riskLevel ?? null;
}

/** Select active penalty tier for a course */
export function selectActiveTier(state: State, courseId: string): PenaltyTier | null {
  const data = getCourseData(state, courseId);
  if (!data) return null;

  const effectiveAbsences = computeEffectiveAbsences(data.entries, data.policy);
  const { activeTier } = resolveActiveTier(data.policy.tiers, effectiveAbsences.totalPenaltyAbsences);
  return activeTier;
}

/** Select calendar data for a course and month */
export function selectCalendarData(
  state: State,
  courseId: string,
  year: number,
  month: number,
): CalendarDay[] {
  const course = state.courses.find((c) => c.id === courseId);
  if (!course) return [];

  const entries = state.entries.filter((e) => e.courseId === courseId);
  const entriesByDate = new Map(entries.map((e) => [e.date, e]));
  const overridesByDate = new Map(course.sessionOverrides.map((o) => [o.date, o]));
  const patternsByDay = new Map(course.meetingPatterns.map((p) => [p.dayOfWeek, p]));

  const grid = getMonthCalendar(year, month);
  const today = todayISO();
  const monthStr = `${year}-${(month + 1).toString().padStart(2, '0')}`;

  const days: CalendarDay[] = [];

  for (const week of grid) {
    for (const dateStr of week) {
      const dayOfWeek = getDayOfWeek(dateStr);
      const pattern = patternsByDay.get(dayOfWeek as 0 | 1 | 2 | 3 | 4 | 5 | 6) ?? null;
      const override = overridesByDate.get(dateStr) ?? null;
      const absenceEntry = entriesByDate.get(dateStr) ?? null;

      // Determine session type
      let sessionType: SessionTypeT | null = null;
      if (override?.type === SessionOverrideType.Holiday || override?.type === SessionOverrideType.Cancelled) {
        sessionType = null; // No session on holidays/cancellations
      } else if (pattern) {
        sessionType = pattern.sessionType;
      } else if (override?.type === SessionOverrideType.MakeUp) {
        sessionType = SessionType.ClassMeeting; // Make-up default
      }

      days.push({
        date: dateStr,
        dayOfWeek,
        sessionType,
        absenceEntry,
        override,
        isCurrentMonth: dateStr.startsWith(monthStr),
        isToday: dateStr === today,
      });
    }
  }

  return days;
}

/** Select courses with budget data for dashboard display */
export function selectCoursesWithBudget(
  state: State,
): Array<{ course: Course; budget: BudgetResult; recovery: RecoveryPath | null }> {
  return state.courses.map((course) => {
    const budget = selectCourseBudget(state, course.id);
    const recovery = selectRecoveryPath(state, course.id);
    return {
      course,
      budget: budget ?? {
        totalAllowed: 0,
        usedUnexcused: 0,
        usedExcused: 0,
        usedTardyConversions: 0,
        usedTotal: 0,
        remaining: 0,
        attendancePercentage: 100,
        activeTier: null,
        nextTier: null,
        absencesToNextTier: 0,
        riskLevel: 'satisfactory' as RiskLevel,
      },
      recovery,
    };
  });
}

// ── Emergent feature selectors ───────────────────────────────────────────

function clamp(min: number, max: number, value: number): number {
  return Math.max(min, Math.min(max, value));
}

/** Per-absence GPA cost for a course (cross-course, credit-weighted). */
export interface PerAbsenceGPACost {
  courseId: string;
  courseName: string;
  creditHours: number;
  riskLevel: RiskLevel;
  gpaCostPerAbsence: number;
  attendancePct: number;
  remaining: number;
}

/**
 * Compute the credit-hour-weighted GPA cost of one more absence for every
 * course that has a base grade. Sorted cheapest-first (ascending).
 */
export function selectPerAbsenceGPACost(state: State): PerAbsenceGPACost[] {
  const rows: Array<{
    courseId: string;
    courseName: string;
    creditHours: number;
    riskLevel: RiskLevel;
    attendancePct: number;
    remaining: number;
    rawGPADelta: number;
  }> = [];

  let totalCredits = 0;

  for (const course of state.courses) {
    if (course.baseGrade === null) continue;
    const data = getCourseData(state, course.id);
    if (!data) continue;

    const budget = selectCourseBudget(state, course.id);
    const effectiveAbsences = computeEffectiveAbsences(data.entries, data.policy);
    const currentImpact = computeGradeImpact(
      course.baseGrade,
      course.gradingScale,
      data.policy,
      effectiveAbsences,
      data.entries,
      course.creditHours,
    );
    const currentGPAPoints = letterGradeToGPA(currentImpact?.currentGrade ?? 'F');

    const sim = simulateFutureAbsences(
      course, data.policy, data.entries, data.totalSessions, 1, course.baseGrade,
    );
    const projectedGPAPoints = letterGradeToGPA(sim.projectedGrade ?? currentImpact?.currentGrade ?? 'F');

    rows.push({
      courseId: course.id,
      courseName: course.name,
      creditHours: course.creditHours,
      riskLevel: budget?.riskLevel ?? RiskLevelEnum.Satisfactory,
      attendancePct: budget?.attendancePercentage ?? 100,
      remaining: budget?.remaining ?? 0,
      rawGPADelta: Math.max(0, currentGPAPoints - projectedGPAPoints),
    });
    totalCredits += course.creditHours;
  }

  const result: PerAbsenceGPACost[] = rows.map((r) => ({
    courseId: r.courseId,
    courseName: r.courseName,
    creditHours: r.creditHours,
    riskLevel: r.riskLevel,
    attendancePct: r.attendancePct,
    remaining: r.remaining,
    gpaCostPerAbsence: totalCredits > 0 ? r.rawGPADelta * (r.creditHours / totalCredits) : 0,
  }));

  return result.sort((a, b) => a.gpaCostPerAbsence - b.gpaCostPerAbsence);
}

// ── TODO-05: Term-Progress "You Are Here" ──────────────────────────────────

export interface TermProgress {
  weeksTotal: number;
  weeksElapsed: number;
  timeFraction: number;
  budgetBurnFraction: number;
  burnRatio: number;
  status: 'on-pace' | 'ahead-of-pace' | 'burning-fast';
}

export function selectTermProgress(state: State, courseId: string): TermProgress | null {
  const data = getCourseData(state, courseId);
  if (!data) return null;
  const budget = selectCourseBudget(state, courseId);

  const msPerWeek = 7 * 86_400_000;
  const weeksTotal = Math.max(
    1,
    Math.round((parseISODate(data.course.endDate).getTime() - parseISODate(data.course.startDate).getTime()) / msPerWeek),
  );
  const weeksElapsed = clamp(
    0,
    weeksTotal,
    Math.round((parseISODate(todayISO()).getTime() - parseISODate(data.course.startDate).getTime()) / msPerWeek),
  );

  const timeFraction = weeksElapsed / weeksTotal;
  const usedTotal = budget?.usedTotal ?? 0;
  const totalAllowed = budget?.totalAllowed ?? 0;
  const budgetBurnFraction = totalAllowed > 0 ? usedTotal / totalAllowed : usedTotal / 1;
  const burnRatio = budgetBurnFraction / Math.max(0.001, timeFraction);

  const status: TermProgress['status'] =
    burnRatio < 0.9 ? 'on-pace' : burnRatio <= 1.1 ? 'ahead-of-pace' : 'burning-fast';

  return { weeksTotal, weeksElapsed, timeFraction, budgetBurnFraction, burnRatio, status };
}

// ── TODO-07: Reason-Aware Pace ─────────────────────────────────────────────

export interface ReasonAwarePace {
  overall: PaceProjection;
  controllableOnly: PaceProjection;
  uncontrollableCount: number;
}

export function selectReasonAwarePace(state: State, courseId: string): ReasonAwarePace | null {
  const data = getCourseData(state, courseId);
  if (!data) return null;

  const { entries, policy, sessions } = data;
  const overall = projectPace(entries, policy, sessions);
  const controllableEntries = entries.filter(
    (e) => e.reason !== AbsenceReason.Illness && e.reason !== AbsenceReason.Family,
  );
  const controllableOnly = projectPace(controllableEntries, policy, sessions);
  const uncontrollableCount = entries.length - controllableEntries.length;

  return { overall, controllableOnly, uncontrollableCount };
}

// ── TODO-10: Time-of-Day Lateness Profile ──────────────────────────────────

export interface LatenessByStartTime {
  startTime: string;
  lateCount: number;
  totalSessions: number;
  latenessRate: number;
}

export function selectLatenessByStartTime(state: State, courseId: string): LatenessByStartTime[] {
  const data = getCourseData(state, courseId);
  if (!data) return [];

  const lateEntries = data.entries.filter((e) => e.type === AbsenceType.Late);
  if (lateEntries.length === 0) return [];

  // Map dayOfWeek → startTime from meeting patterns
  const startByDay = new Map<number, string>();
  for (const pattern of data.course.meetingPatterns) {
    startByDay.set(pattern.dayOfWeek, pattern.startTime);
  }

  // Count late entries per start time
  const lateByStart = new Map<string, number>();
  for (const entry of lateEntries) {
    const dow = getDayOfWeek(entry.date);
    const startTime = startByDay.get(dow);
    if (!startTime) continue;
    lateByStart.set(startTime, (lateByStart.get(startTime) ?? 0) + 1);
  }

  // Count past sessions per start time
  const today = todayISO();
  const sessionsByStart = new Map<string, number>();
  for (const session of data.sessions) {
    if (session.date > today) continue;
    const startTime = session.meetingPattern?.startTime;
    if (!startTime) continue;
    sessionsByStart.set(startTime, (sessionsByStart.get(startTime) ?? 0) + 1);
  }

  const startTimes = new Set<string>([...lateByStart.keys(), ...sessionsByStart.keys()]);
  const rows: LatenessByStartTime[] = [];
  for (const startTime of startTimes) {
    const lateCount = lateByStart.get(startTime) ?? 0;
    const totalSessions = sessionsByStart.get(startTime) ?? 0;
    rows.push({
      startTime,
      lateCount,
      totalSessions,
      latenessRate: lateCount / Math.max(1, totalSessions),
    });
  }
  return rows.sort((a, b) => a.startTime.localeCompare(b.startTime));
}

// ── TODO-11: Participation–Attendance Correlation ──────────────────────────

export interface ParticipationAttendanceCorrelation {
  avgQualityOnTime: number | null;
  avgQualityLate: number | null;
  avgQualityAbsent: number | null;
}

export function selectParticipationAttendanceCorrelation(
  state: State,
  courseId: string,
): ParticipationAttendanceCorrelation {
  const participation = (state.participationEntries ?? []).filter((p) => p.courseId === courseId);
  const entriesByDate = new Map<string, AbsenceEntry>();
  for (const e of state.entries) {
    if (e.courseId === courseId) entriesByDate.set(e.date, e);
  }

  const buckets: Record<'on-time' | 'late' | 'absent', number[]> = {
    'on-time': [],
    late: [],
    absent: [],
  };

  for (const p of participation) {
    const matched = entriesByDate.get(p.date);
    let bucket: keyof typeof buckets = 'on-time';
    if (matched) {
      if (matched.type === AbsenceType.Late || matched.type === AbsenceType.EarlyDeparture) {
        bucket = 'late';
      } else if (matched.type === AbsenceType.Unexcused || matched.type === AbsenceType.Excused) {
        bucket = 'absent';
      }
    }
    buckets[bucket].push(p.quality);
  }

  const avg = (xs: number[]): number | null =>
    xs.length === 0 ? null : xs.reduce((s, x) => s + x, 0) / xs.length;

  return {
    avgQualityOnTime: avg(buckets['on-time']),
    avgQualityLate: avg(buckets.late),
    avgQualityAbsent: avg(buckets.absent),
  };
}

// ── TODO-12: Target-Aware Smart Skip Recommender ───────────────────────────

export interface SmartSkipPlan {
  affordable: number;
  safeToSkip: RankedSession[];
  doNotMiss: RankedSession[];
}

export function selectSmartSkipPlan(
  state: State,
  courseId: string,
  targetGrade: string,
): SmartSkipPlan | null {
  const data = getCourseData(state, courseId);
  if (!data) return null;
  if (data.course.baseGrade === null) return null;

  const affordable = backCalculateMaxAbsences(
    targetGrade,
    data.course.gradingScale,
    data.course.baseGrade,
    data.policy,
    data.entries,
    data.totalSessions,
  );
  const ranked = rankRemainingSessions(data.sessions, data.entries, data.policy);
  const safeToSkip = ranked.slice(0, affordable);
  const doNotMiss = ranked
    .slice(affordable)
    .sort((a, b) => b.riskScore - a.riskScore);

  return { affordable, safeToSkip, doNotMiss };
}

// ── TODO-08: Auto-Fail Date & End-of-Term Grade Trajectory ──────────────────

export interface Trajectory {
  autoFailDate: string | null;
  sessionsUntilAutoFail: number;
  projectedFinalGrade: string | null;
  projectedFinalPoints: number | null;
}

export function selectTrajectory(state: State, courseId: string): Trajectory | null {
  const data = getCourseData(state, courseId);
  if (!data) return null;

  const pace = projectPace(data.entries, data.policy, data.sessions);
  const maxAbsences = computeMaxAbsences(data.policy, data.totalSessions);
  const eff = computeEffectiveAbsences(data.entries, data.policy);

  const { autoFailDate, sessionsUntilAutoFail } = projectAutoFailDate(
    data.sessions, pace.missRate, maxAbsences, eff.totalPenaltyAbsences,
  );
  const { projectedFinalGrade, projectedFinalPoints } = projectFinalGradeAtPace(
    data.course.baseGrade, data.course.gradingScale, data.policy, pace.projectedTotal, data.course.creditHours,
  );

  return { autoFailDate, sessionsUntilAutoFail, projectedFinalGrade, projectedFinalPoints };
}
